// @flow
import React, { useContext, useState } from 'react';
import classnames from 'classnames';

import { t } from 'Src/i18n';
import { useTracking, type TrackingPayload } from 'Tracking/tracking-api/useTracking';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { COLORS } from 'Components/utils/color/colors';
import { Product } from 'Components/widgets/product/Product.jsx';
import { type ProductProps } from 'Components/widgets/product/Product.utils';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { Slider } from 'Components/frameworks/slider/Slider.jsx';
import { Tag } from 'Components/frameworks/tag/Tag';

type Props = {
  tags: $ReadOnlyArray<{
    label: string,
    products: $ReadOnlyArray<{|
      ...$Exact<ProductProps>,
      offer?: Offer,
      onComparisonClick?: () => mixed,
      onClick?: (SyntheticEvent<HTMLElement>) => void,
    |}>,
  }>,
  trackingData: TrackingPayload,
};

export const ProductTags = ({ tags, trackingData }: Props) => {
  // use isDesktop to display slider's controls only on desktops
  const { isTablet, isDesktop } = useContext(ResponsiveContext);
  const [selectedTagIndex, setSelectedTagIndex] = useState(0);
  const track = useTracking(trackingData);

  const { products: selectedProducts, label: selectedTagLabel } = tags[selectedTagIndex];

  return (
    <div className="flex flex-wrap w-full max-w-site">
      <Scrollable
        childrenWrapperClassNames="flex"
        className="w-screen lg:w-full -mx-5 lg:mx-0 mb-4"
      >
        {tags.map((tag, index) => (
          <Tag
            active={index === selectedTagIndex}
            borderColor={index === selectedTagIndex ? COLORS.BLUE : COLORS.GRAY_LIGHT}
            className={classnames('rtl:mr-2 mb-2', { 'rtl:ml-5 lg:rtl:ml-0': index === 0 })}
            color={index === selectedTagIndex ? COLORS.BLUE : COLORS.GRAY_DARKER}
            key={tag.label}
            label={tag.label}
            onClick={() => setSelectedTagIndex(index)}
            trackingData={{ ...trackingData, labelGA: `tag_${tag.label}` }}
            theme="outlinedThin"
          />
        ))}
      </Scrollable>

      <Slider
        className="w-screen lg:w-full -mx-5 lg:mx-0"
        cols={0}
        hasControls={isDesktop && selectedProducts.length > 3}
        nbItemsToScroll={isTablet ? 2 : 1}
        scrollBackwardLabel={t('common.scrollBackward')}
        scrollForwardLabel={t('common.scrollForward')}
        navPosition="center"
        scrollAreaContainerClassName="overflow-x-auto lg:overflow-hidden"
        scrollControlsClassName="-mx-5 lg:mx-0"
      >
        {selectedProducts.map((product, index) => (
          <div
            key={product.id}
            className={classnames('relative rtl:pr-5', {
              'rtl:ml-5 lg:rtl:ml-0': index === 0,
            })}
            style={{ width: '85vw', maxWidth: '380px' }}
          >
            <Product
              {...product}
              onClick={() => track({ labelGA: `${selectedTagLabel}_${product.id}` })}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};
